import React, { useState, useEffect } from 'react';
import { CommonInput } from '@/components/ui/common-input';
import { CommonSelect } from '@/components/ui/common-select';
import MapSelector from '@/components/ports/addports/MapSelector';
import masterDataService from '@/services/masterDataService';

const RetailerProfile = ({
  retailerProfile,
  errors,
  onProfileChange
}) => {
  const [coordinates, setCoordinates] = useState({
    lat: retailerProfile.latitude ? parseFloat(retailerProfile.latitude) : null,
    lng: retailerProfile.longitude ? parseFloat(retailerProfile.longitude) : null
  });

  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);

  useEffect(() => {
    if (retailerProfile.latitude || retailerProfile.longitude) {
      setCoordinates({
        lat: retailerProfile.latitude ? parseFloat(retailerProfile.latitude) : null,
        lng: retailerProfile.longitude ? parseFloat(retailerProfile.longitude) : null
      });
    }
  }, [retailerProfile.latitude, retailerProfile.longitude]);

  // Fetch states
  useEffect(() => {
    const fetchStates = async () => {
      try {
        setLoadingStates(true);
        const statesData = await masterDataService.getStates(true);
        setStates(statesData || []);
      } catch (error) {
        console.error('Failed to fetch states:', error);
      } finally {
        setLoadingStates(false);
      }
    };

    fetchStates();
  }, []);

  // Fetch districts when state changes
  useEffect(() => {
    const fetchDistricts = async () => {
      if (retailerProfile.state_id) {
        try {
          setLoadingDistricts(true);
          const districtsData = await masterDataService.getDistrictsByState(retailerProfile.state_id, true);
          setDistricts(districtsData || []);

          if (retailerProfile.district_id && !districtsData?.some(d => d.id === retailerProfile.district_id)) {
            onProfileChange('retailer_profile', 'district_id', '');
          }
        } catch (error) {
          console.error('Failed to fetch districts:', error);
          setDistricts([]);
        } finally {
          setLoadingDistricts(false);
        }
      } else {
        setDistricts([]);
        onProfileChange('retailer_profile', 'district_id', '');
      }
    };

    fetchDistricts();
  }, [retailerProfile.state_id]);

  const handleLocationSelect = (location) => {
    setCoordinates(location);
    onProfileChange('retailer_profile', 'latitude', location.lat.toString());
    onProfileChange('retailer_profile', 'longitude', location.lng.toString());
  };

  const stateOptions = states.map(state => ({
    value: state.id,
    label: state.name
  }));

  const districtOptions = districts.map(district => ({
    value: district.id,
    label: district.name
  }));

  return (
    <div className="">
      <div className="p-1">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <CommonInput
            label="Company Name"
            id="company_name"
            value={retailerProfile.company_name}
            onChange={(e) => onProfileChange('retailer_profile', 'company_name', e.target.value)}
            error={errors.company_name}
            required
            placeholder="Enter company name"
          />
          <CommonInput
            label="Contact Person"
            id="contact_person"
            value={retailerProfile.contact_person}
            onChange={(e) => onProfileChange('retailer_profile', 'contact_person', e.target.value)}
            placeholder="Enter contact person name"
          />

          <CommonSelect
            label="State"
            id="state_id"
            value={retailerProfile.state_id || ''}
            onValueChange={(value) => {
              onProfileChange('retailer_profile', 'state_id', value);
              onProfileChange('retailer_profile', 'district_id', '');
            }}
            options={stateOptions}
            error={errors.state_id}
            required
            placeholder="Select state"
            searchable
            loading={loadingStates}
          />

          <CommonSelect
            label="District"
            id="district_id"
            value={retailerProfile.district_id || ''}
            onValueChange={(value) => onProfileChange('retailer_profile', 'district_id', value)}
            options={districtOptions}
            error={errors.district_id}
            required
            placeholder="Select district"
            searchable
            loading={loadingDistricts}
            disabled={!retailerProfile.state_id}
          />

          <div className="md:col-span-2">
            <CommonInput
              label="Address"
              id="address"
              value={retailerProfile.address}
              onChange={(e) => onProfileChange('retailer_profile', 'address', e.target.value)}
              placeholder="Enter address"
            />
          </div>
          <div className="md:col-span-2">
            <MapSelector
              onLocationSelect={handleLocationSelect}
              initialLat={coordinates.lat}
              initialLng={coordinates.lng}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetailerProfile;